import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from 'src/environments/environment';

/** Server flag (FeatureFlagNames.NewApp) that opts an FBO, group or everyone into the new app. */
export const NewAppFlag = 'app.new-design';

const RememberedLoginOptInKey = 'newDesignOptIn';

type FeatureFlags = Record<string, boolean> | null | undefined;

/**
 * Resolves the "Try the new design" (Blazor) URL. The host decides where the new app lives,
 * so a single Angular build promotes across environments:
 *   qa-app.fbolinx.com  -> qa-preflight.fbolinx.com
 *   st-app.fbolinx.com  -> st-preflight.fbolinx.com
 *   te-app.fbolinx.com  -> te-preflight.fbolinx.com
 *   www.fbolinx.com     -> preflight.fbolinx.com
 *   support-<x>-app / support -> same as <x>-app / www
 * The feature flag decides who sees it: the link is shown only where the flag resolves on for
 * the FBO the user is working in.
 */
@Injectable({ providedIn: 'root' })
export class NewDesignUrlService {
    private static readonly Domain = '.fbolinx.com';

    constructor(
        private http: HttpClient,
        @Inject('BASE_URL') private baseUrl: string
    ) {}

    /** New-app URL for the host, or '' when the host has no new-app counterpart. */
    resolve(host: string = window.location.hostname): string {
        if (!host.endsWith(NewDesignUrlService.Domain)) {
            return environment.newDesignUrl;
        }
        // Support hosts (AppSettings:SupportUrl, where Conductors open FBOs) have no Blazor host
        // of their own: support-qa-app shares qa-app's, support shares www's.
        const label = host.slice(0, -NewDesignUrlService.Domain.length).replace(/^support-/, '').replace(/^support$/, 'www');
        if (label === 'www') {
            return 'https://preflight.fbolinx.com';
        }
        if (!label.endsWith('app')) {
            return '';
        }
        return `https://${label.replace(/app$/, 'preflight')}.fbolinx.com`;
    }

    forSession(featureFlags: FeatureFlags, host?: string): string {
        return featureFlags?.[NewAppFlag] === true ? this.resolve(host) : '';
    }

    // Before login nobody is known, so the login screen shows the link only on a browser where
    // an opted-in user last signed in.
    forLoginScreen(host?: string): string {
        return this.readRememberedOptIn() ? this.resolve(host) : '';
    }

    rememberLoginOptIn(featureFlags: FeatureFlags): void {
        try {
            if (featureFlags?.[NewAppFlag] === true) {
                localStorage.setItem(RememberedLoginOptInKey, 'true');
            } else {
                localStorage.removeItem(RememberedLoginOptInKey);
            }
        } catch {
            // Storage blocked: the login screen just keeps the link hidden.
        }
    }

    forFbo(fboId: number | null | undefined, groupId: number | null | undefined, host?: string): Observable<string> {
        if (!this.resolve(host)) {
            return of('');
        }
        const params: Record<string, string> = {};
        if (fboId) {
            params.fboId = String(fboId);
        }
        if (groupId) {
            params.groupId = String(groupId);
        }
        return this.http
            .get<{ flags: Record<string, boolean> }>(`${this.baseUrl}api/feature-flags`, { params })
            .pipe(
                map((response) => this.forSession(response?.flags, host)),
                catchError(() => of(''))
            );
    }

    private readRememberedOptIn(): boolean {
        try {
            return localStorage.getItem(RememberedLoginOptInKey) === 'true';
        } catch {
            return false;
        }
    }
}
